/**
 * Some endpoints answer `{ data: T }` and others answer `T` itself; callers
 * that talk to both read either shape through this, instead of an
 * `(x as any)?.data || x` at every call site.
 */
export function unwrapData<T>(
  value: T | { data: T } | null | undefined,
): T | undefined {
  if (value == null) return undefined;
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'data' in (value as object)
  ) {
    return (value as { data: T }).data ?? undefined;
  }
  return value as T;
}

/**
 * A list endpoint's rows and total, whether it answered a bare array or
 * `{ data: [...], total }`. Anything else is an empty list.
 */
export function unwrapList<T>(payload: unknown): { rows: T[]; total: number } {
  if (Array.isArray(payload)) {
    return { rows: payload as T[], total: payload.length };
  }
  const wrapped = payload as { data?: unknown; total?: unknown } | null;
  if (wrapped && Array.isArray(wrapped.data)) {
    return {
      rows: wrapped.data as T[],
      total: Number(wrapped.total) || wrapped.data.length,
    };
  }
  return { rows: [], total: 0 };
}
